import React, { FC, useEffect, useMemo, useState } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { AssetNavigationSidebar, TreeItem, useIntl } from "@metsooutotec/modes-react-components"
import customerStore from "features/organizations/customers/store/customer-store"
import { customerSidebarViewContentStyles } from "../../common/styles/customer-styles"
import LowSpinner from "../../../../components/common/low-spinner"
import { Customer } from "../models/customer-models"
import { AssetSection } from "features/organizations/asset-sections/models/asset-section-models"
import { Asset } from "features/organizations/assets/models/asset-models"
import { Site } from "features/organizations/sites/models/sites-models"

const itemToTreeItem = (text: string, value: string, icon: string, children?: TreeItem[]) => {
    const treeItem = { icon, text, value } as TreeItem

    if (children && children.length) {
        treeItem.children = children
    }

    return treeItem
}

const assetSectionToTreeItem = (section: AssetSection, pathBase: string) => {
    const path = pathBase + "/assetsections/" + section.id
    return itemToTreeItem(section.name, path, "icon-component-16")
}

const assetToTreeItem = (asset: Asset, pathBase: string) => {
    const path = pathBase + "/assets/" + asset.id
    const children = asset.assetSections
        ?.slice()
        .sort((a, b) => a?.name?.localeCompare(b?.name))
        .map(section => assetSectionToTreeItem(section, path))
    return itemToTreeItem(asset.name, path, "icon-equipment-16", children)
}

const siteToTreeItem = (site: Site, pathBase: string) => {
    const path = pathBase + "/sites/" + site.id
    const children = site.assets
        ?.slice()
        .sort((a, b) => a?.name?.localeCompare(b?.name))
        .map(asset => assetToTreeItem(asset, path))
    return itemToTreeItem(site.name, path, "icon-location-o-16", children)
}

const customerToTreeItem = (customer: Customer) => {
    const path = "/customers/" + customer.id
    const children = customer.sites
        ?.slice()
        .sort((a, b) => a?.name?.localeCompare(b?.name))
        .map(site => siteToTreeItem(site, path))
    return itemToTreeItem(customer.name, path, "icon-person-o-16", children)
}

const search = (filterExpr: string, items: TreeItem[]) => {
    const regex = new RegExp(filterExpr, "i")

    return items.reduce((acc, item) => {
        if (item.text.search(regex) >= 0) {
            return [...acc, item]
        }

        const children = search(filterExpr, item.children || []) as TreeItem[]
        if (children.length) {
            return [...acc, { ...item, children } as TreeItem]
        }

        return acc
    }, [] as TreeItem[])
}

const getOpenItemsAfterSearch = (filterExpr: string, items: TreeItem[]): string[] => [
    ...items.filter(item => item.children && item.children.some(c => c.text.search(new RegExp(filterExpr, "i")) >= 0)).map(item => item.value),
    ...items.map(item => getOpenItemsAfterSearch(filterExpr, item.children || [])).flat(),
]
interface CustomerSidebarViewComponentProps {
    tree: TreeItem[]
}
const CustomerSidebarViewComponent: FC<CustomerSidebarViewComponentProps> = ({ tree }) => {
    const { formatMessage } = useIntl()
    const currentLocation = useLocation()
    const navigate = useNavigate()
    const [openItems, setOpenItems] = useState<string[]>([])
    const [searchTerm, setSearchTerm] = useState("")
    const [sidebarOpen, setSidebarOpen] = useState(true)

    //search section
    const items = useMemo(() => {
        return searchTerm ? search(searchTerm, tree) : tree
    }, [searchTerm, tree])
    useEffect(() => {
        if (searchTerm) {
            setOpenItems(getOpenItemsAfterSearch(searchTerm, items))
        } else {
            setOpenItems([])
        }
    }, [searchTerm, items])
    useEffect(() => {
        // this trick only to make all elments inside the list to be closed
        if (sidebarOpen) {
            setOpenItems(prevOpenItems => [...prevOpenItems, "your-item"])
        } else {
            setOpenItems([])
        }
    }, [sidebarOpen])
    return (
        <AssetNavigationSidebar
            title={formatMessage("customerSidebar.title")}
            assetNavigationProps={{
                openItems: [...openItems],
            }}
            items={items}
            onChange={(vals: string[]) => navigate(vals[0])}
            onHeaderClick={() => setSidebarOpen(!sidebarOpen)}
            search={true}
            searchOnChange={e => setSearchTerm(e.target.value)}
            searchProps={{ placeholder: formatMessage("customerSidebar.searchPlaceholder"), value: searchTerm }}
            selectedItems={[currentLocation.pathname]}
            visible={sidebarOpen}
        >
            <AssetNavigationSidebar.Content style={customerSidebarViewContentStyles}>
                <Outlet />
            </AssetNavigationSidebar.Content>
        </AssetNavigationSidebar>
    )
}

const CustomerSidebarView: FC = observer(() => {
    const { formatMessage } = useIntl()
    const { customers } = customerStore
    
    //here to inject side items
    const tree = useMemo(() => {
        const headerTreeItem = {
            icon: "icon-home-16",
            text: formatMessage("customerSidebar.allCustomers"),
            value: "/customers",
        } as TreeItem
        return [
            headerTreeItem,
            ...customers
                .slice()
                .sort((a, b) => a?.name?.localeCompare(b?.name))
                .map(customerToTreeItem),
        ]
    }, [JSON.stringify(customers)])

    return tree.length <= 1 ? <LowSpinner id={"customer.loading"} /> : <CustomerSidebarViewComponent tree={tree} />
})

export default CustomerSidebarView
